import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Modal, Slider, Radio, RadioChangeEvent } from 'antd';
import { FileImageOutlined, ScissorOutlined } from '@ant-design/icons';
import { Button } from '../button';
import Cropper from 'react-easy-crop';
import { Area, Point } from 'react-easy-crop/types';
import MainBanner from '../main-banner';
import { Acvt } from '../advanced-ct-visualisation-tool';
import { AcvtHomePage } from '../advanced-ct-visualisation-tool/home-page';
import { PreviewProvider, PreviewThemeProvider } from '../preview';
import { UNKNOWN_BACKGROUND_RED } from '../../styles/colors';
import { ApplicationTargetEnum } from '../../types/app-selection';
import style from './style.module.scss';

// Aspect ratio presets for image cropping
const ASPECT_RATIO_PRESETS = [
    { label: 'Card (Default)', value: 305 / 180, key: 'card' },
    { label: '16:9', value: 16 / 9, key: '16-9' },
    { label: '4:3', value: 4 / 3, key: '4-3' },
    { label: '1:1', value: 1, key: '1-1' },
    { label: '3:4', value: 3 / 4, key: '3-4' },
    { label: '9:16', value: 9 / 16, key: '9-16' },
] as const;

interface ImageCropModalProps {
    visible: boolean;
    imageSrc: string;
    aspectRatio?: number;
    onCancel: () => void;
    onComplete: (croppedBlob: Blob) => void;
    onSkipCrop?: (originalBlob: Blob) => void;
    allowSkipCrop?: boolean;
    isLegacyMode?: boolean; // Legacy mode shows simple image, new mode shows MainBanner
    previewData?: {
        backgroundColor?: string | null;
        title?: string;
        titleColor?: string | null;
        subtitle?: string;
        cta?: string;
    };
    uploadSessionId?: number; // Unique ID per upload to force component remount
}

const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image), { once: true });
        image.addEventListener('error', (error) => reject(error), {
            once: true,
        });
        // Only set crossOrigin for external URLs to prevent canvas tainting with data/blob URLs
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
            image.setAttribute('crossOrigin', 'anonymous');
        }
        image.src = url;
    });

async function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<Blob> {
    // Validate all crop coordinates are finite numbers
    if (
        !Number.isFinite(pixelCrop.x) ||
        !Number.isFinite(pixelCrop.y) ||
        !Number.isFinite(pixelCrop.width) ||
        !Number.isFinite(pixelCrop.height)
    ) {
        throw new Error(
            `Invalid crop coordinates: x=${pixelCrop.x}, y=${pixelCrop.y}, width=${pixelCrop.width}, height=${pixelCrop.height}`
        );
    }

    if (
        !pixelCrop.width ||
        !pixelCrop.height ||
        pixelCrop.width <= 0 ||
        pixelCrop.height <= 0
    ) {
        throw new Error(
            `Invalid crop dimensions: ${pixelCrop.width}x${pixelCrop.height}`
        );
    }

    const image = await createImage(imageSrc);

    if (
        !image.complete ||
        !image.naturalWidth ||
        !image.naturalHeight ||
        image.naturalWidth === 0 ||
        image.naturalHeight === 0
    ) {
        throw new Error('Image failed to load properly');
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        throw new Error('No 2d context');
    }

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    );

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Canvas is empty - toBlob returned null'));
                return;
            }
            resolve(blob);
        }, 'image/jpeg');
    });
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
    const response = await fetch(dataUrl);
    return await response.blob();
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({
    visible,
    imageSrc,
    aspectRatio = 305 / 2 / 180, // Image section is 50% of card width (152.5px x 180px)
    onCancel,
    onComplete,
    onSkipCrop,
    allowSkipCrop = false, // Default to false for backwards compatibility
    isLegacyMode = false,
    previewData,
    uploadSessionId = 0, // Default to 0 for backwards compatibility
}) => {
    // Track the last uploadSessionId to detect changes during render
    // Initialize with -1 to ensure reset happens on first render
    const lastUploadSessionIdRef = useRef<number>(-1);

    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [selectedAspectRatio, setSelectedAspectRatio] = useState<number>(
        aspectRatio
    );
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(
        null
    );
    const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);
    const [isCropperReady, setIsCropperReady] = useState(false);

    // Track blob URL for proper cleanup without race conditions
    const blobUrlRef = useRef<string | null>(null);

    // Reset modal state synchronously during render when uploadSessionId changes
    // This must happen BEFORE the Cropper component renders with the state
    if (lastUploadSessionIdRef.current !== uploadSessionId) {
        lastUploadSessionIdRef.current = uploadSessionId;
        // Reset all state to initial values synchronously
        // Using conditional checks to avoid unnecessary state updates
        if (crop.x !== 0 || crop.y !== 0) {
            setCrop({ x: 0, y: 0 });
        }
        if (zoom !== 1) {
            setZoom(1);
        }
        if (croppedAreaPixels !== null) {
            setCroppedAreaPixels(null);
        }
        if (croppedImageUrl !== null) {
            setCroppedImageUrl(null);
        }
        if (isCropperReady) {
            setIsCropperReady(false);
        }
        // Reset aspect ratio to initial prop value
        if (selectedAspectRatio !== aspectRatio) {
            setSelectedAspectRatio(aspectRatio);
        }
    }

    // Delay Cropper rendering to ensure modal container has stable dimensions
    useEffect(() => {
        if (visible) {
            // Small delay to let modal fully render and stabilize
            const timer = setTimeout(() => {
                setIsCropperReady(true);
            }, 50);
            return () => clearTimeout(timer);
        } else {
            setIsCropperReady(false);
        }
    }, [visible, uploadSessionId]);

    const handleZoomChange = (newZoom: number) => {
        setZoom(newZoom);
    };

    const handleAspectRatioChange = (e: RadioChangeEvent) => {
        const newRatio = e.target.value as number;
        setSelectedAspectRatio(newRatio);
    };

    const handleCropChange = (newCrop: Point) => {
        // Only update crop if values are valid (not NaN)
        // The Cropper might send NaN during initialization or transitions
        if (Number.isFinite(newCrop.x) && Number.isFinite(newCrop.y)) {
            setCrop(newCrop);
        } else {
            console.warn('[CROPPER] Ignoring invalid crop position:', newCrop);
        }
    };

    const onCropComplete = useCallback(
        async (_croppedArea: Area, croppedAreaPixels: Area) => {
            // Only update croppedAreaPixels if all values are valid (not NaN)
            // This prevents storing invalid crop data that would break Apply Crop
            if (
                !Number.isFinite(croppedAreaPixels.x) ||
                !Number.isFinite(croppedAreaPixels.y) ||
                !Number.isFinite(croppedAreaPixels.width) ||
                !Number.isFinite(croppedAreaPixels.height)
            ) {
                // Silently skip - the Cropper is still initializing or in transition
                return;
            }

            // Update with valid coordinates
            setCroppedAreaPixels(croppedAreaPixels);

            try {
                const croppedBlob = await getCroppedImg(
                    imageSrc,
                    croppedAreaPixels
                );
                const previewUrl = URL.createObjectURL(croppedBlob);

                // Revoke old blob URL if it exists
                if (blobUrlRef.current) {
                    URL.revokeObjectURL(blobUrlRef.current);
                }

                // Track new blob URL
                blobUrlRef.current = previewUrl;
                setCroppedImageUrl(previewUrl);
            } catch (e) {
                console.error('Error generating preview:', e);
            }
        },
        [imageSrc]
    );

    const handleApplyCrop = async () => {
        if (!croppedAreaPixels) {
            console.warn('[CROP] No crop area defined');
            return;
        }

        // Validate coordinates before attempting crop
        if (
            !Number.isFinite(croppedAreaPixels.x) ||
            !Number.isFinite(croppedAreaPixels.y) ||
            !Number.isFinite(croppedAreaPixels.width) ||
            !Number.isFinite(croppedAreaPixels.height)
        ) {
            console.error(
                '[CROP] Cannot apply crop: invalid coordinates (NaN detected)',
                croppedAreaPixels
            );
            return;
        }

        try {
            const croppedBlob = await getCroppedImg(
                imageSrc,
                croppedAreaPixels
            );

            // Cleanup blob URL before completing
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }

            onComplete(croppedBlob);
        } catch (e) {
            console.error('[CROP] Error creating cropped image:', e);
        }
    };

    const handleSkipCrop = async () => {
        try {
            const originalBlob = await dataUrlToBlob(imageSrc);

            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }

            if (onSkipCrop) {
                onSkipCrop(originalBlob);
            } else {
                onComplete(originalBlob);
            }
        } catch (e) {
            console.error('[CROP] Error processing original image:', e);
        }
    };

    const handleCancel = () => {
        // Cleanup blob URL on cancel
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setSelectedAspectRatio(aspectRatio);
        setCroppedAreaPixels(null);
        setCroppedImageUrl(null);
        setIsCropperReady(false);
        onCancel();
    };

    return (
        <Modal
            title="Crop Image"
            open={visible}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" variant="secondary" onClick={handleCancel}>
                    Cancel
                </Button>,
                ...(allowSkipCrop
                    ? [
                          <Button
                              key="skip"
                              variant="secondary"
                              onClick={handleSkipCrop}
                              icon={<FileImageOutlined />}
                          >
                              Use Original
                          </Button>,
                      ]
                    : []),
                <Button
                    key="apply"
                    variant="primary"
                    onClick={handleApplyCrop}
                    icon={<ScissorOutlined />}
                >
                    Apply Crop
                </Button>,
            ]}
            width={1400}
            className={style.crop_modal}
        >
            <div className={style.modal_content}>
                <div className={style.cropper_section}>
                    <div className={style.cropper_container}>
                        {isCropperReady && (
                            <Cropper
                                key={`cropper-${uploadSessionId}`}
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={selectedAspectRatio}
                                onCropChange={handleCropChange}
                                onZoomChange={handleZoomChange}
                                onCropComplete={onCropComplete}
                            />
                        )}
                    </div>
                    <div className={style.controls}>
                        <label>Aspect Ratio</label>
                        <Radio.Group
                            value={selectedAspectRatio}
                            onChange={handleAspectRatioChange}
                            buttonStyle="solid"
                            size="middle"
                        >
                            {ASPECT_RATIO_PRESETS.map((preset) => (
                                <Radio.Button
                                    key={preset.key}
                                    value={preset.value}
                                >
                                    {preset.label}
                                </Radio.Button>
                            ))}
                        </Radio.Group>
                        <label>Zoom</label>
                        <Slider
                            min={1}
                            max={3}
                            step={0.1}
                            value={zoom}
                            onChange={handleZoomChange}
                        />
                    </div>
                </div>
                <div className={style.preview_section}>
                    <div className={style.preview_title}>App Preview</div>
                    <div className={style.preview_wrapper}>
                        {!isLegacyMode ? (
                            <PreviewProvider
                                initialRegion={ApplicationTargetEnum.QC}
                            >
                                <PreviewThemeProvider
                                    region={ApplicationTargetEnum.QC}
                                >
                                    <Acvt
                                        key={`acvt-${uploadSessionId}`}
                                        backgroundColor={UNKNOWN_BACKGROUND_RED}
                                    >
                                        <AcvtHomePage
                                            onEditMode={true}
                                            hideHeader={true}
                                            featuredCarousel={
                                                <MainBanner
                                                    key={`banner-${uploadSessionId}`}
                                                    image={
                                                        croppedImageUrl ||
                                                        imageSrc
                                                    }
                                                    backgroundColor={
                                                        previewData?.backgroundColor
                                                    }
                                                    title={previewData?.title}
                                                    titleColor={
                                                        previewData?.titleColor
                                                    }
                                                    subtitle={
                                                        previewData?.subtitle
                                                    }
                                                    cta={previewData?.cta}
                                                />
                                            }
                                        />
                                    </Acvt>
                                </PreviewThemeProvider>
                            </PreviewProvider>
                        ) : (
                            <img
                                key={`legacy-${uploadSessionId}`}
                                src={croppedImageUrl || imageSrc}
                                alt="Cropped preview"
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    borderRadius: '8px',
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ImageCropModal;
